import { availableCombatOptions, type CombatState } from '../../../packages/sim/src/combat/index.ts';
import type { PcCommand } from '../../../packages/sim/src/pc-actions/index.ts';
import { PC_ID } from './scene.ts';

/** The fresh state an order is about to be applied to, inside the commit that
 * queued behind the tick save already in flight when the player gave it. */
export type FreshOrderState = Readonly<{ combat: CombatState; fighting: boolean; dead: boolean }>;

const pcDead = (fresh: FreshOrderState): boolean => fresh.dead || fresh.combat.actors[PC_ID]?.status === 'dead';

/**
 * An order is checked against the screen, then queued behind the save already
 * in flight. If that tick killed the order's target, ended the fight or killed
 * the player, applying the order to the fresh state is rejected by the rules
 * ('Choose a living opponent', 'A dead PC cannot issue actions', posthumous
 * action) and the error screen replaces the victory or death beat. Such an
 * order is stale: the caller drops it quietly instead of applying it.
 * `wasFighting` is the screen state the order was given against.
 */
export function stalePcOrder(command: PcCommand, fresh: FreshOrderState, wasFighting: boolean): boolean {
  if (pcDead(fresh)) return true;
  if (command.type === 'danger-options') return !fresh.fighting;
  if (command.type === 'pause') return wasFighting && !fresh.fighting;
  const action = command.type === 'act' || command.type === 'queue' ? command.action : null;
  if (!action || action.type === 'move') return false;
  if (!fresh.fighting) return true;
  if ('targetId' in action) {
    const pc = fresh.combat.actors[PC_ID], target = fresh.combat.actors[action.targetId];
    if (!pc || !target || target.status === 'dead' || fresh.combat.tactics?.actors[target.id]?.escaped
      || (target.role === 'enemy') === (pc.role === 'enemy')) return true;
  }
  if (action.type === 'consume' && !availableCombatOptions(fresh.combat).consumables.includes(action.itemId)) return true;
  return false;
}

/** A walk given during a fight is stale once that tick has ended the fight (or
 * the player); a walk given outside a fight stays an ordinary exploration walk. */
export function staleWalk(fresh: FreshOrderState, wasFighting: boolean): boolean {
  return pcDead(fresh) || (wasFighting && !fresh.fighting);
}
